import React, { useEffect, Children, Fragment } from 'react';
import { motion } from 'framer-motion';
interface ShopsAdsTrustSystemProps {
  title: string;
  gridLineColor?: string;
}
export function ShopsAdsTrustSystem({
  title,
  gridLineColor = 'rgba(185,210,214,0.5)'
}: ShopsAdsTrustSystemProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const container = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };
  const item = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.19, 1, 0.22, 1]
      }
    }
  };
  return <motion.article variants={container} initial="hidden" animate="show" className="w-full pb-[0px]" data-id="element-365" data-mp="1e5pr90mqg7gs">
      {/* Header Section */}
      <div className="max-w-4xl mx-auto px-6 md:px-12 mb-16 md:mb-24 text-center pt-12" data-id="element-366" data-mp="1o46o2e8crgar">
        <motion.h1 variants={item} className="text-4xl md:text-6xl font-bold text-ink tracking-tight mb-6" data-id="element-367" data-mp="2tgyegg2a25yd">
          {title}
        </motion.h1>
        <motion.p variants={item} className="text-xl md:text-2xl text-stone-medium font-light leading-relaxed max-w-3xl mx-auto mb-16" data-id="element-368" data-mp="2xjv06fn46i6j">
          Led product strategy for Community Content in Shops Ads, turning
          fragmented community content into a scalable trust layer that drives
          conversion.
        </motion.p>

        {/* Metadata Row */}
        <motion.div variants={item} className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left border-t border-b border-stone-border py-8" data-id="element-369" data-mp="3getc17k7y91j">
          <div data-id="element-370" data-mp="3q5e7wn8iczgr">
            <h3 className="text-sm font-bold text-ink mb-2" data-id="element-371" data-mp="3q5xljrr4l1oz">My role</h3>
            <p className="text-stone-medium text-sm" data-id="element-372" data-mp="3v73hrzjzv739">
              Design lead (3 designers)
            </p>
          </div>
          <div data-id="element-373" data-mp="3zqy9dvn2hk6">
            <h3 className="text-sm font-bold text-ink mb-2" data-id="element-374" data-mp="3x4qpe8r021y">Time frame</h3>
            <p className="text-stone-medium text-sm" data-id="element-375" data-mp="3ah0yfio7ervg">
              Q4 22' - Q1 23' (6 months)
            </p>
          </div>
          <div data-id="element-376" data-mp="35ftqnjm29sos">
            <h3 className="text-sm font-bold text-ink mb-2" data-id="element-377" data-mp="3k6ash42lykm7">Platform</h3>
            <p className="text-stone-medium text-sm" data-id="element-378" data-mp="3k8neptfefh52">
              Mobile + Advertiser platforms
            </p>
          </div>
        </motion.div>
      </div>

      {/* Context and Challenge Modules */}
      <div className="max-w-3xl mx-auto px-6 md:px-12 mb-24 space-y-16" data-id="element-379" data-mp="3t927fduozyil">
        <motion.section variants={item} data-id="element-380" data-mp="3o7wb761tpt4b">
          <h2 className="text-2xl font-bold text-ink mb-4" data-id="element-381" data-mp="6zr67avxv49x">Context</h2>
          <p className="text-stone-medium leading-relaxed mb-6" data-id="element-382" data-mp="6xy6ygnnnmby">
            Commerce is a key growth driver for Meta, with Shops Ads positioned
            to unlock high-value, signal-resilient revenue. At the same time,
            shopping behavior has evolved:
          </p>
          <ul className="list-disc pl-6 text-stone-medium leading-relaxed space-y-[2px]" data-id="element-383" data-mp="23tvp1o9n0nzg">
            <li data-id="element-384" data-mp="jbl904jfve1z">Buyers rely on social proof & community signals</li>
            <li data-id="element-385" data-mp="o9v0o55yfwiu">Shopping journeys are non-linear and multi-touch</li>
            <li data-id="element-386" data-mp="ob64u63bb6ah">
              Trust is built progressively—not just at product detail page
            </li>
          </ul>
        </motion.section>

        <motion.section variants={item} data-id="element-387" data-mp="eiibxmfgdmh1">
          <h2 className="text-2xl font-bold text-ink mb-4" data-id="element-388" data-mp="ehh0hguiyxys">Challenge</h2>
          <p className="text-stone-medium leading-relaxed mb-6" data-id="element-389" data-mp="i7iqcqo7vsp">
            Community Content (UGC, reviews, Q&A) plays a critical role in
            influencing purchase decisions and improving conversion. However,
            within Shops Ads, Community Content was treated as fragmented
            features—primarily concentrated in the consideration phase—limiting
            its ability to build trust and drive conversion across the full
            shopping journey.
          </p>
        </motion.section>
      </div>

      {/* Fragmented trust signal image */}
      <motion.div variants={item} className="w-full flex justify-center px-6 md:px-12 mb-24" data-id="element-390" data-mp="3m23kr0xogtgk">
        <img src="https://cdn.magicpatterns.com/uploads/1iSBr6keS8CK7TKyJAP42N/Fragmented_trust_signal.jpg" alt="Fragmented trust signals across buyer journey" className="w-full max-w-[1200px] h-auto object-contain" data-id="element-391" data-mp="3h41zqagoyk2g" />
      </motion.div>

      {/* Key Insight */}
      <div className="max-w-3xl mx-auto px-6 md:px-12 mb-24" data-id="element-392" data-mp="333r998jan7ht">
        <motion.section variants={item} data-id="element-393" data-mp="2ybr3gzgjlkdb">
          <h2 className="text-2xl font-bold text-ink mb-4" data-id="element-394" data-mp="2yapssw3fuzw4">Key Insight</h2>
          <p className="text-stone-medium leading-relaxed mb-6" data-id="element-395" data-mp="2te7q01ro5bmf">
            Community Content already existed—but it wasn't working as a system.
            Trust signals were scattered, inconsistent, and surfaced too late,
            forcing users to interpret them on their own.
          </p>
          <p className="text-stone-medium leading-relaxed" data-id="element-396" data-mp="2fd54bq4l63c1">
            This reframed the problem from improving content to designing a
            trust system across the entire shopping journey.
          </p>
        </motion.section>
      </div>

      {/* Trust System Strategy */}
      <motion.section variants={item} className="mb-24 mt-[96px] pt-[96px]" data-id="element-397" data-mp="14t0kc7dij5bi">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center mb-12" data-id="element-398" data-mp="18v5agw4r5jte">
          <h2 className="text-3xl font-bold text-ink mb-4" data-id="element-399" data-mp="18uc9ycwdzqjp">
            Trust System Strategy
          </h2>
          <p className="text-stone-medium mb-8" data-id="element-400" data-mp="c2s1edb5kgvv">
            Define how trust signals should be introduced, evaluated, and
            reinforced across the journey—progressively building user confidence
          </p>
        </div>
      </motion.section>

      {/* Theme — Trust Signal Integration */}
      <motion.section variants={item} className="mb-24" data-id="element-401" data-mp="py44b7zgpdnv">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-402" data-mp="3pizos6vcmw9d">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-403" data-mp="3tl4g2yybrgsu">
            <div className="lg:col-span-4" data-id="element-404" data-mp="3tj01nn2suzeb">
              <h2 className="text-2xl font-bold text-ink mb-4" data-id="element-405" data-mp="10q4bwp7kcvmv">
                Early trust signals (Discovery)
              </h2>
              <p className="text-stone-medium leading-relaxed" data-id="element-406" data-mp="vrj6mbdgflrk">
                Surface lightweight, high-signal indicators (e.g. ratings,
                reviews) early in the journey to help users quickly assess
                credibility and filter options.
              </p>
            </div>
            <div className="lg:col-span-8 w-full" data-id="element-407" data-mp="hsjrbubu0j82">
              <div className="w-full rounded-md overflow-hidden flex justify-center" data-id="element-408" data-mp="ctgngd7l8j1o">
                <img src="https://cdn.magicpatterns.com/uploads/rNFzbVT2Vdagp4c8h3pfTm/Pillar_1.jpg" alt="Unified trust signal system across shopping journey" className="w-[75%] h-auto object-contain" data-id="element-409" data-mp="qsgcvn2xd1yw" />
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* New section above impact */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-32" data-id="element-410" data-mp="25q2gvueff0cv">
        <motion.section variants={item} className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-411" data-mp="25ohni83ulngl">
          <div className="lg:col-span-4" data-id="element-412" data-mp="1vrv1vh3p6sdi">
            <h2 className="text-2xl font-bold text-ink mb-4" data-id="element-413" data-mp="1vswomiu2t3b2">
              Scannable, decision-oriented content (Consideration)
            </h2>
            <p className="text-stone-medium leading-relaxed" data-id="element-414" data-mp="2ogaejwzcwylo">
              Condense community signals into structured, bite-sized formats
              that support quick comparison and reduce cognitive load during
              evaluation.
            </p>
          </div>
          <div className="lg:col-span-8 lg:order-first w-full" data-id="element-415" data-mp="2eq7exdxqcmed">
            <div className="w-[85%] mx-auto rounded-md overflow-hidden" data-id="element-416" data-mp="2join7k8dmouh">
              <img src="https://cdn.magicpatterns.com/uploads/9JfyrqvGbUVcCpjruBgRY9/Pillar_2.jpg" alt="Community-led discovery shopping experience" className="w-full h-auto" data-id="element-417" data-mp="37fel8z3fega6" />
            </div>
          </div>
        </motion.section>
      </div>

      {/* Third pillar section */}
      <motion.section variants={item} className="mb-24" data-id="element-418" data-mp="17bc31rq8blh4">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-419" data-mp="13b1viamsgn05">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-420" data-mp="13bjuhf0u5fbr">
            <div className="lg:col-span-4" data-id="element-421" data-mp="ydrzgc4w9d3c">
              <h2 className="text-2xl font-bold text-ink mb-4" data-id="element-422" data-mp="1v2hl8djn6pul">
                Community-led discovery
              </h2>
              <p className="text-stone-medium leading-relaxed" data-id="element-423" data-mp="1r0mhl2e8o0dn">
                Leverage creators, buyers, and social signals to surface
                relevant products through trusted voices, turning discovery into
                a social and credibility-driven experience.
              </p>
            </div>
            <div className="lg:col-span-8 w-full" data-id="element-424" data-mp="annuo1fyz7ar">
              <div className="w-full rounded-md overflow-hidden flex justify-center" data-id="element-425" data-mp="ak9wz6i93m9n">
                <img src="https://cdn.magicpatterns.com/uploads/22iHdSE9tbmH1PrvM7wSoY/Pillar_3.jpg" alt="Reinforced trust signals at decision points" className="w-[75%] h-auto object-contain" data-id="element-426" data-mp="5pmfvspktu53" />
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Impact and outcome */}
      <motion.section variants={item} className="w-full bg-ink text-white py-32 px-6 md:px-12" data-id="element-427" data-mp="a9tratewu0lf">
        <div className="max-w-4xl mx-auto text-center" data-id="element-428" data-mp="ecgmn4asi3nd">
          <h2 className="text-3xl md:text-4xl font-bold mb-8" data-id="element-429" data-mp="j7vyxrt5frho">
            Impact and outcome
          </h2>
          <p className="text-xl text-stone-medium font-light leading-relaxed" data-id="element-430" data-mp="o8th44e5slj6">
            Influenced how trust signals are prioritized across the journey,
            shifting from feature-level optimizations to a system-level approach
            and enabling more consistent, confidence-driven purchase decisions.
          </p>
        </div>
      </motion.section>
    </motion.article>;
}